<template>
  <div class="page-container">
    <el-tabs v-model="activeTab">
      <el-tab-pane label="系统监控" name="monitor">
        <div class="monitor-grid">
          <div class="monitor-card">
            <div class="card-header">
              <el-icon><Cpu /></el-icon>
              <span>CPU使用率</span>
            </div>
            <div class="card-content">
              <div class="progress-wrapper">
                <el-progress :percentage="systemStats.cpu" :color="getProgressColor(systemStats.cpu)" :stroke-width="16" />
              </div>
              <div class="stats-value">{{ systemStats.cpu }}%</div>
              <div class="stats-sub" v-if="systemStats.cpuCores">物理核 {{ systemStats.cpuCores }} / 逻辑核 {{ systemStats.cpuThreads }}</div>
            </div>
          </div>
          <div class="monitor-card">
            <div class="card-header">
              <el-icon><Monitor /></el-icon>
              <span>内存使用率</span>
            </div>
            <div class="card-content">
              <div class="progress-wrapper">
                <el-progress :percentage="systemStats.memory" :color="getProgressColor(systemStats.memory)" :stroke-width="16" />
              </div>
              <div class="stats-value">{{ systemStats.memory }}%</div>
              <div class="stats-sub" v-if="systemStats.memoryTotal">{{ systemStats.memoryUsed }} / {{ systemStats.memoryTotal }}</div>
            </div>
          </div>
          <div class="monitor-card">
            <div class="card-header">
              <el-icon><FolderOpened /></el-icon>
              <span>磁盘使用率</span>
            </div>
            <div class="card-content">
              <div class="progress-wrapper">
                <el-progress :percentage="systemStats.disk" :color="getProgressColor(systemStats.disk)" :stroke-width="16" />
              </div>
              <div class="stats-value">{{ systemStats.disk }}%</div>
              <div class="stats-sub" v-if="systemStats.diskTotal">{{ systemStats.diskUsed }} / {{ systemStats.diskTotal }}（{{ systemStats.diskMount }}）</div>
            </div>
          </div>
          <div class="monitor-card">
            <div class="card-header">
              <el-icon><Connection /></el-icon>
              <span>网络状态</span>
            </div>
            <div class="card-content">
              <div class="status-indicator">
                <span :class="['status-dot', systemStats.network === '正常' ? 'status-normal' : 'status-error']"></span>
                <span class="status-text">{{ systemStats.network }}</span>
              </div>
              <div class="stats-sub" v-if="systemStats.networkLatency">延迟 {{ systemStats.networkLatency }}</div>
            </div>
          </div>
        </div>
        <div class="monitor-detail">
          <div class="detail-card">
            <div class="detail-title">服务器</div>
            <div class="detail-row"><span class="detail-label">操作系统：</span><span>{{ systemStats.os || '—' }}</span></div>
            <div class="detail-row"><span class="detail-label">主机名：</span><span>{{ systemStats.hostname || '—' }}</span></div>
            <div class="detail-row"><span class="detail-label">架构：</span><span>{{ systemStats.arch || '—' }}</span></div>
            <div class="detail-row"><span class="detail-label">运行时长：</span><span>{{ formatUptime(systemStats.systemUptime) }}</span></div>
          </div>
          <div class="detail-card">
            <div class="detail-title">JVM</div>
            <div class="detail-row"><span class="detail-label">Java 版本：</span><span>{{ systemStats.jvmVersion || '—' }}</span></div>
            <div class="detail-row"><span class="detail-label">JVM：</span><span>{{ systemStats.jvmName || '—' }}</span></div>
            <div class="detail-row"><span class="detail-label">JVM 内存：</span><span>{{ systemStats.jvmMemoryUsed || '—' }} / {{ systemStats.jvmMemoryMax || '—' }}（{{ systemStats.jvmMemoryUsage || 0 }}%）</span></div>
            <div class="detail-row"><span class="detail-label">刷新频率：</span><span>每 5 秒自动更新</span></div>
          </div>
        </div>
      </el-tab-pane>
      <el-tab-pane label="系统配置" name="config">
        <el-form :model="configForm" label-width="150px">
          <el-form-item label="系统显示名称" :label-width="formLabelWidth">
            <el-input v-model="configForm.systemName" placeholder="请输入系统显示名称" />
          </el-form-item>
          <el-form-item label="可视化名称" :label-width="formLabelWidth">
            <el-input v-model="configForm.dashboardName" placeholder="用于资产大屏的标题" />
          </el-form-item>
          <el-form-item label="系统版本号" :label-width="formLabelWidth">
            <el-input v-model="configForm.systemVersion" placeholder="请输入系统版本号" />
          </el-form-item>
          <el-form-item label="版权信息" :label-width="formLabelWidth">
            <el-input v-model="configForm.copyright" placeholder="请输入版权信息" />
          </el-form-item>
          <el-form-item label="首页标题" :label-width="formLabelWidth">
            <el-input v-model="configForm.homeTitle" placeholder="请输入首页标题" />
          </el-form-item>
          <el-form-item label="系统LOGO" :label-width="formLabelWidth">
            <div class="logo-upload">
              <el-upload
                class="logo-uploader"
                :show-file-list="false"
                :http-request="uploadLogo"
                :before-upload="beforeLogoUpload"
                accept="image/*">
                <img v-if="configForm.logoUrl" :src="configForm.logoUrl" class="logo-img" alt="LOGO" />
                <el-icon v-else class="logo-uploader-icon"><Plus /></el-icon>
              </el-upload>
              <div class="logo-side">
                <div class="logo-tip">建议上传 PNG/SVG 正方形图标，≤ 2MB</div>
                <el-button v-if="configForm.logoUrl" size="small" text type="danger" @click="configForm.logoUrl = ''">移除当前LOGO</el-button>
              </div>
            </div>
          </el-form-item>
          <el-form-item>
            <el-button type="primary" @click="saveConfig">保存配置</el-button>
          </el-form-item>
        </el-form>
      </el-tab-pane>
      <el-tab-pane label="推送配置" name="push">
        <div class="push-toolbar">
          <el-button type="primary" @click="openPushDialog()">新增推送配置</el-button>
          <span class="push-tip">按推送场景配置：定时推送按每日设定时间自动执行（如合同到期提醒）；触发推送在业务事件发生时立即推送（如工单接收、告警分级）。推送场景可持续扩展。</span>
        </div>
        <el-table stripe :data="pushConfigs" border>
          <el-table-column prop="pushName" label="推送名称" min-width="130" />
          <el-table-column label="推送场景" min-width="140">
            <template #default="scope">{{ sceneName(scope.row.pushCode) }}</template>
          </el-table-column>
          <el-table-column label="推送方式" min-width="170">
            <template #default="scope">
              <template v-if="scope.row.pushCode === 'patrol_remind'">
                <el-tag size="small">提前 {{ (JSON.parse(scope.row.params || '{}').beforeMinutes || 30) }} 分钟</el-tag>
              </template>
              <template v-else-if="scope.row.pushType === 'timed'">
                <el-tag size="small">定时 {{ scope.row.pushTime || '—' }}</el-tag>
              </template>
              <template v-else>
                <el-tag type="warning" size="small">触发 {{ scope.row.eventCode || '—' }}</el-tag>
              </template>
            </template>
          </el-table-column>
          <el-table-column label="启用状态" width="90">
            <template #default="scope">
              <el-tag :type="scope.row.enabled === 1 ? 'success' : 'info'" size="small">{{ scope.row.enabled === 1 ? '已启用' : '已停用' }}</el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="remark" label="备注" min-width="120" show-overflow-tooltip />
          <el-table-column label="操作" width="220" fixed="right">
            <template #default="scope">
              <el-button size="small" @click="openPushDialog(scope.row)">编辑</el-button>
              <el-button size="small" type="danger" @click="handleDeletePush(scope.row)">删除</el-button>
              <el-button size="small" type="primary" plain @click="openLogDialog(scope.row)">推送日志</el-button>
            </template>
          </el-table-column>
        </el-table>

        <el-dialog :title="pushForm.id ? '编辑推送配置' : '新增推送配置'" v-model="pushDialogVisible" width="640px">
          <el-form :model="pushForm" label-width="130px">
            <el-form-item label="推送场景" required>
              <el-select v-model="pushForm.pushCode" :disabled="!!pushForm.id" style="width: 100%" placeholder="请选择推送场景" @change="onSceneChange">
                <el-option v-for="(s, code) in PUSH_SCENES" :key="code" :label="s.name" :value="code" />
              </el-select>
              <div v-if="sceneDesc(pushForm.pushCode)" class="push-inline-tip">{{ sceneDesc(pushForm.pushCode) }}</div>
            </el-form-item>
            <el-form-item label="推送名称" required>
              <el-input v-model="pushForm.pushName" placeholder="请输入推送名称" />
            </el-form-item>
            <el-form-item label="是否启用">
              <el-switch v-model="pushForm.enabledBool" active-text="启用" inactive-text="停用" />
            </el-form-item>
            <el-form-item v-if="sceneDef && sceneDef.pushType === 'timed' && sceneDef.pushTime !== null" label="每日推送时间">
              <el-time-picker v-model="pushForm.pushTime" value-format="HH:mm" format="HH:mm" placeholder="选择推送时间" style="width: 140px" />
              <span class="push-inline-tip">每天到该时间点自动执行推送</span>
            </el-form-item>
            <el-form-item v-else-if="sceneDef && sceneDef.pushType === 'event'" label="触发方式">
              <el-tag type="warning" size="small">事件触发：{{ sceneDef.eventCode }}</el-tag>
              <span class="push-inline-tip">业务事件发生时立即推送，无需设置时间</span>
            </el-form-item>
            <el-form-item v-for="f in sceneFields" :key="f.key" :label="f.label">
              <template v-if="f.type === 'number'">
                <el-input-number v-model="pushForm.paramsData[f.key]" :min="1" :max="365" />
                <span class="push-inline-tip">{{ f.tip }}</span>
              </template>
              <template v-else-if="f.type === 'expireRules'">
                <div class="rule-groups">
                  <div class="rule-group">
                    <div class="rule-group-title">到期前提醒</div>
                    <div v-for="(r, i) in pushForm.paramsData[f.key].beforeRules" :key="'b' + i" class="rule-row">
                      <span class="rule-text">到期前</span>
                      <el-input-number v-model="r.days" :min="1" :max="365" size="small" controls-position="right" style="width: 100px" />
                      <span class="rule-text">天通知</span>
                      <el-select v-model="r.receiverIds" multiple filterable placeholder="接收人(可多选)" size="small" style="flex: 1">
                        <el-option v-for="u in userOptions" :key="u.id" :label="userLabel(u)" :value="u.id" />
                      </el-select>
                      <el-button type="danger" link size="small" @click="pushForm.paramsData[f.key].beforeRules.splice(i, 1)">删除</el-button>
                    </div>
                    <el-button size="small" @click="addExpireRule(f.key, 'beforeRules')">+ 添加到期前提醒</el-button>
                  </div>
                  <div class="rule-group">
                    <div class="rule-group-title">到期当天提醒</div>
                    <el-select v-model="pushForm.paramsData[f.key].expireDayReceivers" multiple filterable placeholder="接收人(可多选)，不需要则留空" style="width: 100%">
                      <el-option v-for="u in userOptions" :key="u.id" :label="userLabel(u)" :value="u.id" />
                    </el-select>
                  </div>
                  <div class="rule-group">
                    <div class="rule-group-title">过期后提醒</div>
                    <div v-for="(r, i) in pushForm.paramsData[f.key].afterRules" :key="'a' + i" class="rule-row">
                      <span class="rule-text">过期</span>
                      <el-input-number v-model="r.days" :min="1" :max="365" size="small" controls-position="right" style="width: 100px" />
                      <span class="rule-text">天通知</span>
                      <el-select v-model="r.receiverIds" multiple filterable placeholder="接收人(可多选)" size="small" style="flex: 1">
                        <el-option v-for="u in userOptions" :key="u.id" :label="userLabel(u)" :value="u.id" />
                      </el-select>
                      <el-button type="danger" link size="small" @click="pushForm.paramsData[f.key].afterRules.splice(i, 1)">删除</el-button>
                    </div>
                    <el-button size="small" @click="addExpireRule(f.key, 'afterRules')">+ 添加过期提醒</el-button>
                  </div>
                  <div class="push-inline-tip">按"天"精确匹配：到期前第 N 天 / 当天 / 过期第 N 天，命中当天通知一次；多个节点可通知不同的人</div>
                </div>
              </template>
              <template v-else-if="f.type === 'users'">
                <el-select v-model="pushForm.paramsData[f.key]" multiple filterable placeholder="可多选接收用户" style="width: 100%">
                  <el-option v-for="u in userOptions" :key="u.id" :label="userLabel(u)" :value="u.id" />
                </el-select>
              </template>
              <template v-else>
                <el-input v-model="pushForm.paramsData[f.key]" :placeholder="f.tip || '选填'" />
              </template>
            </el-form-item>            
            <el-form-item label="备注">
              <el-input v-model="pushForm.remark" type="textarea" :rows="2" placeholder="选填" />
            </el-form-item>
          </el-form>
          <template #footer>
            <el-button @click="pushDialogVisible = false">取消</el-button>
            <el-button type="primary" @click="handleSavePush">保存</el-button>
          </template>
        </el-dialog>

        <transition name="log-fade">
          <div v-if="logDialogVisible" class="log-mask">
            <div class="log-panel">
              <div class="log-title">
                <span>推送日志 · {{ logCurrentConfig.pushName || '—' }}</span>
                <el-button text circle @click="logDialogVisible = false">
                  <el-icon :size="18"><Close /></el-icon>
                </el-button>
              </div>
              <div class="log-body">
                <div class="log-dialog-head">
                  <el-select v-model="logStatusFilter" placeholder="状态" size="small" clearable style="width: 120px" @change="reloadPushLogs">
                    <el-option label="全部" value="" />
                    <el-option label="成功" value="success" />
                    <el-option label="部分失败" value="partial" />
                    <el-option label="失败" value="failed" />
                  </el-select>
                </div>
                <el-table stripe :data="logList" border v-loading="logLoading" @expand-change="onLogExpand">
                  <el-table-column type="expand">
                    <template #default="scope">
                      <div class="log-detail">
                        <!-- 接收人 -->
                        <div class="log-detail-row">
                          <span class="log-detail-label">接收人：</span>
                          <div class="log-receivers">
                            <template v-if="scope.row._items">
                              <template v-if="scope.row._items.length === 0">
                                <span class="log-detail-text">—</span>
                              </template>
                              <el-tooltip v-for="item in scope.row._items" :key="item.id"
                                :content="item.status === 'fail' ? (item.errorMsg || '推送失败') : '推送成功'" placement="top">
                                <el-tag :type="item.status === 'success' ? 'success' : 'danger'" size="small" style="margin-right:6px;margin-bottom:4px">
                                  {{ item.receiverName || '未命名' }}{{ item.username ? '(' + item.username + ')' : '' }}
                                </el-tag>
                              </el-tooltip>
                            </template>
                            <span v-else class="log-detail-text">加载中...</span>
                          </div>
                        </div>
                        <div class="log-detail-row">
                          <span class="log-detail-label">消息内容预览：</span>
                          <pre class="log-detail-pre">{{ scope.row.content || '—' }}</pre>
                        </div>
                        <div class="log-detail-row">
                          <span class="log-detail-label">错误信息：</span>
                          <span class="log-detail-text" :class="{ 'log-err': scope.row.errorMsg }">{{ scope.row.errorMsg || '无' }}</span>
                        </div>
                        <div class="log-detail-row">
                          <span class="log-detail-label">完成时间：</span>
                          <span>{{ scope.row.finishTime ? formatDateTime(scope.row.finishTime) : '—' }}</span>
                        </div>
                        <div class="log-detail-row">
                          <span class="log-detail-label">耗时：</span>
                          <span>{{ scope.row.durationMs != null ? scope.row.durationMs + ' ms' : '—' }}</span>
                        </div>
                      </div>
                    </template>
                  </el-table-column>
                  <el-table-column label="执行时间" width="180">
                    <template #default="scope">{{ formatDateTime(scope.row.runTime) }}</template>
                  </el-table-column>
                  <el-table-column prop="title" label="标题" min-width="160" show-overflow-tooltip />
                  <el-table-column label="类型" width="100">
                    <template #default="scope">
                      <el-tag size="small" :type="scope.row.pushType === 'timed' ? 'primary' : 'warning'">{{ scope.row.pushType === 'timed' ? '定时' : '触发' }}</el-tag>
                      <el-tag size="small" :type="scope.row.mode === 'real' ? 'success' : 'info'" style="margin-left:4px">{{ scope.row.mode === 'real' ? '正式' : '模拟' }}</el-tag>
                    </template>
                  </el-table-column>
                  <el-table-column label="接收情况" width="120">
                    <template #default="scope">
                      {{ scope.row.success || 0 }}/{{ scope.row.total || 0 }}
                      <span v-if="scope.row.fail" class="log-fail"> (失败{{ scope.row.fail }})</span>
                    </template>
                  </el-table-column>
                  <el-table-column label="状态" width="90">
                    <template #default="scope">
                      <el-tag :type="scope.row.status === 'success' ? 'success' : scope.row.status === 'partial' ? 'warning' : 'danger'" size="small">{{ logStatusText(scope.row.status) }}</el-tag>
                    </template>
                  </el-table-column>
                </el-table>
                <MyPager v-if="logTotal > 0" :total="logTotal" v-model:page="logCurrent" v-model:size="logPageSize" @change="onLogPageChange" />
                <div v-if="!logLoading && logList.length === 0" class="log-empty">暂无推送记录</div>
              </div>
            </div>
          </div>
        </transition>
      </el-tab-pane>
    </el-tabs>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, onUnmounted, watch } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { Cpu, Monitor, FolderOpened, Connection, Plus } from '@element-plus/icons-vue'
import { getConfigList, saveConfigBatch, uploadFile, getPushConfigList, savePushConfig, deletePushConfig, getUserList, getPushLogList, getPushLogDetail } from '../api'
import request from '../utils/request'
import { sysConfig } from '../utils/sysConfig'
import { formatDateTime } from '../utils/format'
import MyPager from '../components/MyPager.vue'

const activeTab = ref('monitor')
const formLabelWidth = '150px'

const systemStats = reactive({
  cpu: 0,
  cpuCores: 0,
  cpuThreads: 0,
  memory: 0,
  memoryTotal: '',
  memoryUsed: '',
  disk: 0,
  diskTotal: '',
  diskUsed: '',
  diskMount: '',
  network: '检测中',
  networkLatency: '',
  os: '',
  hostname: '',
  arch: '',
  jvmMemoryUsed: '',
  jvmMemoryMax: '',
  jvmMemoryUsage: 0,
  jvmVersion: '',
  jvmName: '',
  systemUptime: 0
})

let monitorTimer = null
const MONITOR_REFRESH_MS = 5000

const loadSystemMonitor = async () => {
  try {
    const res = await request.get('/system/monitor')
    Object.assign(systemStats, res.data || {})
  } catch (e) {
    console.error('加载系统监控失败', e)
  }
}

const configForm = reactive({
  systemName: '粮库资产管理平台',
  dashboardName: '粮库资产可视化平台',
  systemVersion: '1.0.0',
  copyright: '© 2024 粮库资产管理平台',
  homeTitle: '欢迎使用粮库资产管理平台',
  logoUrl: ''
})

const configList = ref([])

// ===== 推送配置 =====
// 推送场景注册表：新增推送场景只需在此加一个条目（含表单字段定义），前后端无需改表结构
// type: number=数字输入 users=多选用户 text=文本
const PUSH_SCENES = {
  contract_expire: {
    name: '合同到期提醒',
    pushType: 'timed',
    desc: '每日定时扫描执行中的合同，可配置多个提醒节点：到期前N天（可多条）、到期当天、过期N天，各节点可设不同接收人',
    fields: [
      { key: 'rules', label: '提醒节点', type: 'expireRules' }
    ]
  },
  workorder_receive: {
    name: '工单接收提醒',
    pushType: 'event',
    eventCode: 'workorder_created',
    desc: '新工单创建后立即推送给指定接收人',
    fields: [
      { key: 'receiverIds', label: '接收人', type: 'users', def: [] }
    ]
  },
  alarm_level: {
    name: '告警分级接收',
    pushType: 'event',
    eventCode: 'alarm_raised',
    desc: '告警产生后按告警级别推送给对应级别的接收人',
    fields: [
      { key: 'urgentReceiverIds', label: '紧急告警接收人', type: 'users', def: [] },
      { key: 'majorReceiverIds', label: '重大告警接收人', type: 'users', def: [] },
      { key: 'generalReceiverIds', label: '一般告警接收人', type: 'users', def: [] }
    ]
  },
  patrol_remind: {
    name: '巡检任务提醒',
    pushType: 'timed',
    pushTime: null,     // patrol_remind 不需要每日推送时间，每小时自动扫描
    desc: '每小时扫描巡检计划执行时间，到达提前量时自动生成巡检任务并推送巡检人员（按岗位 patrol_member 路由）',
    fields: [
      { key: 'beforeMinutes', label: '提前(分钟)', type: 'number', def: 30, min: 1, max: 1440 }
    ]
  }
}

const pushConfigs = ref([])
const pushDialogVisible = ref(false)
const userOptions = ref([])
const pushForm = reactive({
  id: null,
  pushCode: '',
  pushName: '',
  enabledBool: false,
  pushTime: '08:00',
  paramsData: {},
  remark: ''
})

const sceneDef = computed(() => PUSH_SCENES[pushForm.pushCode] || null)
const sceneFields = computed(() => (sceneDef.value ? sceneDef.value.fields : []))
const sceneName = (code) => (PUSH_SCENES[code] ? PUSH_SCENES[code].name : code)
const sceneDesc = (code) => (PUSH_SCENES[code] ? PUSH_SCENES[code].desc : '')

// 按场景字段定义初始化 paramsData 默认值
const initParamsData = () => {
  const obj = {}
  sceneFields.value.forEach(f => {
    if (f.type === 'expireRules') {
      obj[f.key] = { beforeRules: [], expireDayReceivers: [], afterRules: [] }
    } else {
      obj[f.key] = f.def !== undefined ? f.def : ''
    }
  })
  pushForm.paramsData = obj
}

const addExpireRule = (key, listName) => {
  if (!pushForm.paramsData[key]) return
  pushForm.paramsData[key][listName].push({ days: listName === 'beforeRules' ? 30 : 10, receiverIds: [] })
}

// 接收人ids字符串 -> 数字数组
const toUserArr = (v) => (v ? String(v).split(',').map(Number).filter(n => !isNaN(n)) : [])

const onSceneChange = () => {
  if (!pushForm.id && sceneDef.value) pushForm.pushName = sceneDef.value.name
  initParamsData()
}

const userLabel = (u) => {
  const name = u.realName || u.username || ('用户' + u.id)
  return u.username && u.username !== name ? name + '（' + u.username + '）' : name
}

const loadPushConfigs = async () => {
  try {
    const res = await getPushConfigList()
    pushConfigs.value = res.data || []
  } catch (e) {
    console.error('加载推送配置失败', e)
  }
}

const loadUserOptions = async () => {
  try {
    const res = await getUserList()
    userOptions.value = res.data || []
  } catch (e) {
    console.error('加载用户列表失败', e)
  }
}

const openPushDialog = (row) => {
  if (row) {
    pushForm.id = row.id
    pushForm.pushCode = row.pushCode || ''
    pushForm.pushName = row.pushName || ''
    pushForm.enabledBool = row.enabled === 1
    pushForm.pushTime = row.pushTime || '08:00'
    pushForm.remark = row.remark || ''
    initParamsData()
    // 回填 params JSON 到动态字段
    let params = {}
    if (row.params) {
      try { params = JSON.parse(row.params) } catch (e) { params = {} }
    }
    sceneFields.value.forEach(f => {
      const v = params[f.key]
      if (f.type === 'expireRules') {
        const r = (v && typeof v === 'object') ? v : {}
        pushForm.paramsData[f.key] = {
          beforeRules: (r.beforeRules || []).map(x => ({ days: Number(x.days) || 1, receiverIds: toUserArr(x.receiverIds) })),
          expireDayReceivers: toUserArr(r.expireDayReceivers),
          afterRules: (r.afterRules || []).map(x => ({ days: Number(x.days) || 1, receiverIds: toUserArr(x.receiverIds) }))
        }
      } else if (f.type === 'users') {
        pushForm.paramsData[f.key] = toUserArr(v)
      } else if (f.type === 'number') {
        pushForm.paramsData[f.key] = (v !== undefined && v !== null && v !== '') ? Number(v) : (f.def !== undefined ? f.def : '')
      } else {
        pushForm.paramsData[f.key] = v !== undefined && v !== null ? String(v) : ''
      }
    })
  } else {
    pushForm.id = null
    pushForm.pushCode = ''
    pushForm.pushName = ''
    pushForm.enabledBool = true
    pushForm.pushTime = '08:00'
    pushForm.remark = ''
    pushForm.paramsData = {}
  }
  pushDialogVisible.value = true
}

const handleSavePush = async () => {
  if (!pushForm.pushCode) {
    ElMessage.warning('请选择推送场景')
    return
  }
  if (!pushForm.pushName.trim()) {
    ElMessage.warning('请填写推送名称')
    return
  }
  const def = sceneDef.value
  if (def && def.pushType === 'timed' && def.pushTime !== null && !pushForm.pushTime) {
    ElMessage.warning('请设置每日推送时间')
    return
  }
  // 校验：合同到期提醒至少配置一个提醒节点及接收人
  const ruleField = sceneFields.value.find(f => f.type === 'expireRules')
  if (ruleField) {
    const r = pushForm.paramsData[ruleField.key] || {}
    const hasRule = (r.beforeRules || []).some(x => (x.receiverIds || []).length > 0)
      || (r.expireDayReceivers || []).length > 0
      || (r.afterRules || []).some(x => (x.receiverIds || []).length > 0)
    if (!hasRule) {
      ElMessage.warning('请至少配置一个提醒节点及接收人')
      return
    }
  }
  // 组装 params JSON：users 数组转逗号字符串，其余按原类型
  const params = {}
  sceneFields.value.forEach(f => {
    const v = pushForm.paramsData[f.key]
    if (f.type === 'expireRules') {
      const r = v || {}
      params[f.key] = {
        beforeRules: (r.beforeRules || []).map(x => ({ days: x.days, receiverIds: (x.receiverIds || []).join(',') })),
        expireDayReceivers: (r.expireDayReceivers || []).join(','),
        afterRules: (r.afterRules || []).map(x => ({ days: x.days, receiverIds: (x.receiverIds || []).join(',') }))
      }
    } else if (f.type === 'users') {
      params[f.key] = Array.isArray(v) ? v.join(',') : String(v || '')
    } else {
      params[f.key] = v
    }
  })
  try {
    await savePushConfig({
      id: pushForm.id,
      pushName: pushForm.pushName.trim(),
      pushCode: pushForm.pushCode,
      pushType: def ? def.pushType : 'timed',
      eventCode: def && def.pushType === 'event' ? def.eventCode : null,
      enabled: pushForm.enabledBool ? 1 : 0,
      pushTime: def && def.pushType === 'timed' ? pushForm.pushTime : null,
      params: JSON.stringify(params),
      remark: pushForm.remark
    })
    ElMessage.success('推送配置保存成功')
    pushDialogVisible.value = false
    loadPushConfigs()
  } catch (e) {
    ElMessage.error('保存失败：' + (e && e.message ? e.message : e))
  }
}

const handleDeletePush = async (row) => {
  try {
    await ElMessageBox.confirm(
      `确定删除推送配置「${row.pushName}」吗？删除后该推送将不再执行。`,
      '删除确认',
      { type: 'warning', confirmButtonText: '删除', cancelButtonText: '取消' }
    )
  } catch (cancel) {
    return
  }
  try {
    await deletePushConfig(row.id)
    ElMessage.success('删除成功')
    loadPushConfigs()
  } catch (e) {
    ElMessage.error('删除失败')
  }
}

// ===== 推送日志查看 =====
const logDialogVisible = ref(false)
const logList = ref([])
const logTotal = ref(0)
const logCurrent = ref(1)
const logPageSize = ref(20)
const logLoading = ref(false)
const logStatusFilter = ref('')
const logCurrentConfig = ref({})

// 弹出时锁定页面滚动
watch(logDialogVisible, (v) => {
  const content = document.querySelector('.content')
  if (!content) return
  if (v) { content.style.overflow = 'hidden' } else { content.style.overflow = '' }
})

const logStatusText = (s) => (s === 'success' ? '成功' : s === 'partial' ? '部分失败' : s === 'failed' ? '失败' : (s || '—'))

const openLogDialog = async (row) => {
  logCurrentConfig.value = row || {}
  logStatusFilter.value = ''
  logCurrent.value = 1
  logDialogVisible.value = true
  await loadPushLogs()
}

const loadPushLogs = async () => {
  logLoading.value = true
  try {
    const params = {
      page: logCurrent.value,
      size: logPageSize.value,
      pushCode: logCurrentConfig.value.pushCode
    }
    if (logStatusFilter.value) params.status = logStatusFilter.value
    const res = await getPushLogList(params)
    const data = res.data || {}
    logList.value = data.records || []
    logTotal.value = data.total || 0
  } catch (e) {
    console.error('加载推送日志失败', e)
    logList.value = []
    logTotal.value = 0
  } finally {
    logLoading.value = false
  }
}

const reloadPushLogs = () => {
  logCurrent.value = 1
  loadPushLogs()
}

// 展开行时懒加载接收人明细
const onLogExpand = async (row, expandedRows) => {
  const isExpanded = expandedRows.some(r => r.id === row.id)
  if (!isExpanded || row._items) return  // 收起或已加载过
  try {
    const res = await getPushLogDetail(row.id)
    row._items = (res.data && res.data.items) || []
  } catch (e) {
    console.error('加载推送明细失败', e)
    row._items = []
  }
}

const onLogPageChange = (page, size) => {
  if (size != null) logPageSize.value = size
  logCurrent.value = page
  loadPushLogs()
}

const getProgressColor = (percentage) => {
  if (percentage < 50) return '#67c23a'
  if (percentage < 80) return '#e6a23c'
  return '#f56c6c'
}

const formatUptime = (seconds) => {
  if (!seconds || seconds <= 0) return '—'
  const d = Math.floor(seconds / 86400)
  const h = Math.floor((seconds % 86400) / 3600)
  const m = Math.floor((seconds % 3600) / 60)
  const parts = []
  if (d > 0) parts.push(d + ' 天')
  if (h > 0) parts.push(h + ' 小时')
  if (m > 0) parts.push(m + ' 分钟')
  return parts.join(' ') || '< 1 分钟'
}

onMounted(() => {
  loadConfig()
  loadPushConfigs()
  loadUserOptions()
  // 进入页面立即加载一次系统监控
  loadSystemMonitor()
  monitorTimer = setInterval(loadSystemMonitor, MONITOR_REFRESH_MS)
})

onUnmounted(() => {
  if (monitorTimer) {
    clearInterval(monitorTimer)
    monitorTimer = null
  }
})

const loadConfig = async () => {
  try {
    const res = await getConfigList()
    configList.value = res.data
    configList.value.forEach(config => {
      if (config.configKey === 'system_name') configForm.systemName = config.configValue || ''
      if (config.configKey === 'dashboard_name') configForm.dashboardName = config.configValue || ''
      if (config.configKey === 'system_version') configForm.systemVersion = config.configValue || ''
      if (config.configKey === 'copyright') configForm.copyright = config.configValue || ''
      if (config.configKey === 'home_title') configForm.homeTitle = config.configValue || ''
      if (config.configKey === 'system_logo') configForm.logoUrl = config.configValue || ''
    })
  } catch (error) {
    console.error('加载配置失败', error)
  }
}

const beforeLogoUpload = (file) => {
  const isImage = file.type && file.type.startsWith('image/')
  const isLt2M = file.size / 1024 / 1024 < 2
  if (!isImage) {
    ElMessage.error('只能上传图片文件（PNG/JPG/SVG 等）')
    return false
  }
  if (!isLt2M) {
    ElMessage.error('图片大小不能超过 2MB')
    return false
  }
  return true
}

const uploadLogo = async (option) => {
  const formData = new FormData()
  formData.append('file', option.file)
  try {
    const res = await uploadFile(formData, 'logo')
    configForm.logoUrl = res.data
    option.onSuccess(res)
    ElMessage.success('LOGO 上传成功')
  } catch (e) {
    option.onError(e)
    ElMessage.error('LOGO 上传失败')
  }
}

const saveConfig = async () => {
  try {
    const keys = ['system_name', 'dashboard_name', 'system_version', 'copyright', 'home_title', 'system_logo']
    const values = {
      system_name: configForm.systemName,
      dashboard_name: configForm.dashboardName,
      system_version: configForm.systemVersion,
      copyright: configForm.copyright,
      home_title: configForm.homeTitle,
      system_logo: configForm.logoUrl
    }
    // 按 key 构造完整配置列表：库里已存在则沿用其 id（走更新），否则只带 configKey（走新增）
    const toSave = keys.map(key => {
      const exist = configList.value.find(c => c.configKey === key)
      const item = exist ? { ...exist } : { configKey: key }
      item.configValue = values[key] != null ? values[key] : ''
      return item
    })
    await saveConfigBatch(toSave)
    // 实时同步到全局 store，使管理系统 / 登录页 / 大屏立即生效
    sysConfig.system_name = configForm.systemName
    sysConfig.dashboard_name = configForm.dashboardName
    sysConfig.system_logo = configForm.logoUrl
    ElMessage.success('配置保存成功')
  } catch (error) {
    ElMessage.error('保存失败')
  }
}
</script>

<style scoped>
.monitor-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;
}

.monitor-card {
  background: white;
  padding: 24px;
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
}

.card-header {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 16px;
  font-weight: 600;
  color: #303133;
  margin-bottom: 20px;
}

.card-content {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.progress-wrapper {
  width: 100%;
  margin-bottom: 12px;
}

.stats-value {
  font-size: 28px;
  font-weight: bold;
  color: #303133;
}

.stats-sub {
  font-size: 12px;
  color: #909399;
  margin-top: 6px;
}

.monitor-detail {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
  margin-top: 20px;
}

.detail-card {
  background: white;
  padding: 20px 24px;
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
}

.detail-title {
  font-size: 14px;
  font-weight: 600;
  color: #409eff;
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 1px solid #ebeef5;
}

.detail-row {
  display: flex;
  font-size: 13px;
  color: #606266;
  margin-bottom: 6px;
  line-height: 1.6;
  word-break: break-all;
}

.detail-label {
  flex-shrink: 0;
  color: #909399;
  min-width: 88px;
}

.status-indicator {
  display: flex;
  align-items: center;
  gap: 12px;
}

.status-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
}

.status-normal {
  background: #67c23a;
  animation: pulse 2s infinite;
}

.status-error {
  background: #f56c6c;
}

@keyframes pulse {
  0% { box-shadow: 0 0 0 0 rgba(103, 194, 58, 0.7); }
  70% { box-shadow: 0 0 0 10px rgba(103, 194, 58, 0); }
  100% { box-shadow: 0 0 0 0 rgba(103, 194, 58, 0); }
}

.status-text {
  font-size: 28px;
  font-weight: bold;
  color: #303133;
}

.logo-upload {
  display: flex;
  align-items: flex-start;
  gap: 16px;
}

.logo-uploader {
  width: 100px;
  height: 100px;
  border: 1px dashed #d9d9d9;
  border-radius: 8px;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fafafa;
  flex-shrink: 0;
}

.logo-uploader:hover {
  border-color: #409eff;
}

.logo-uploader-icon {
  font-size: 28px;
  color: #8c939d;
}

.logo-img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
}

.logo-side {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-top: 6px;
}

.logo-tip {
  font-size: 12px;
  color: #909399;
  line-height: 1.5;
}

.push-toolbar {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-bottom: 12px;
}

.push-tip {
  font-size: 12px;
  color: #909399;
  line-height: 1.5;
}

.push-inline-tip {
  font-size: 12px;
  color: #909399;
  margin-left: 8px;
}
.rule-groups {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.rule-group {
  border: 1px solid #e4e7ed;
  border-radius: 6px;
  padding: 10px 12px;
  background: #fafbfc;
}
.rule-group-title {
  font-size: 13px;
  font-weight: bold;
  color: #303133;
  margin-bottom: 8px;
}
.rule-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}
.rule-text {
  font-size: 13px;
  color: #606266;
  white-space: nowrap;
}
.push-inline-tip {
  font-size: 12px;
  color: #909399;
  margin-left: 10px;
}
.log-dialog-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}
.log-dialog-title {
  font-size: 14px;
  font-weight: 600;
  color: #303133;
}
.log-fail {
  color: #f56c6c;
  font-size: 12px;
}
.log-detail {
  padding: 8px 16px;
  background: #fafbfc;
}
.log-detail-row {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-bottom: 8px;
  font-size: 13px;
  color: #606266;
  line-height: 1.6;
}
.log-detail-label {
  flex-shrink: 0;
  color: #909399;
  width: 96px;
}
.log-detail-pre {
  margin: 0;
  white-space: pre-wrap;
  word-break: break-all;
  font-family: Consolas, Monaco, monospace;
  font-size: 12px;
  color: #303133;
  background: #fff;
  border: 1px solid #ebeef5;
  border-radius: 4px;
  padding: 6px 8px;
  flex: 1;
  max-height: 200px;
  overflow: auto;
}
.log-detail-text.log-err {
  color: #f56c6c;
}
.log-empty {
  text-align: center;
  color: #909399;
  padding: 40px 0;
  font-size: 13px;
}

/* ===== 推送日志面板 — 覆盖主内容区（Layout.vue: sidebar=220px, header=64px, content padding=20px） ===== */
.log-mask {
  position: fixed;
  left: 240px;
  top: 84px;
  right: 20px;
  bottom: 20px;
  z-index: 999;
  background: #fff;
  border: 1px solid #dcdfe6;
  border-radius: 6px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.12);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.log-panel {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.log-title {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 20px;
  font-size: 16px;
  font-weight: 600;
  color: #303133;
  background: linear-gradient(90deg, #ecf5ff, #fff);
  border-bottom: 1px solid #ebeef5;
}
.log-body {
  flex: 1;
  min-height: 0;
  padding: 16px 20px;
  overflow: auto;
  display: flex;
  flex-direction: column;
}
/* transition */
.log-fade-enter-active,
.log-fade-leave-active {
  transition: opacity 0.2s ease;
}
.log-fade-enter-from,
.log-fade-leave-to {
  opacity: 0;
}
</style>
